import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { zh, en } from "./content";
import styles from "./index.module.scss";
import architecture from "@/assets/images/about/openga/architecture.webp";
import community from "@/assets/images/about/openga/community.webp";
import contactMap from "@/assets/images/about/openga/contact-map.webp";
import coverEarth from "@/assets/images/about/openga/cover-earth.webp";
import ecology from "@/assets/images/about/openga/ecology.webp";
import energy from "@/assets/images/about/openga/energy.webp";
import featureCompute from "@/assets/images/about/openga/feature-compute.webp";
import featureData from "@/assets/images/about/openga/feature-data.webp";
import featureOpen from "@/assets/images/about/openga/feature-open.webp";
import featureReason from "@/assets/images/about/openga/feature-reason.webp";
import geomcp from "@/assets/images/about/openga/geomcp.webp";
import introEarth from "@/assets/images/about/openga/intro-earth.webp";
import knowledge from "@/assets/images/about/openga/knowledge.webp";
import lowAltitude from "@/assets/images/about/openga/low-altitude.webp";
import mascot from "@/assets/images/about/openga/mascot.webp";
import naturalResources from "@/assets/images/about/openga/natural-resources.webp";
import qrcode from "@/assets/images/about/openga/qrcode.webp";
import skillhub from "@/assets/images/about/openga/skillhub.webp";
import standards from "@/assets/images/about/openga/standards.webp";

const images = {
  architecture, community, ecology, energy, geomcp, knowledge, mascot, qrcode, skillhub, standards,
  "contact-map": contactMap,
  "cover-earth": coverEarth,
  "feature-compute": featureCompute,
  "feature-data": featureData,
  "feature-open": featureOpen,
  "feature-reason": featureReason,
  "intro-earth": introEarth,
  "low-altitude": lowAltitude,
  "natural-resources": naturalResources,
};
const imageSizes = {
  "architecture": [670, 385],
  "community": [770, 440],
  "contact-map": [1645, 1450],
  "cover-earth": [1651, 1710],
  "ecology": [865, 440],
  "energy": [860, 435],
  "feature-compute": [198, 190],
  "feature-data": [185, 170],
  "feature-open": [200, 185],
  "feature-reason": [200, 190],
  "geomcp": [590, 285],
  "intro-earth": [1650, 916],
  "knowledge": [435, 285],
  "low-altitude": [860, 425],
  "mascot": [1000, 880],
  "natural-resources": [860, 440],
  "qrcode": [284, 282],
  "skillhub": [495, 285],
  "standards": [745, 445],
};
const asset = (name) => images[name];
const Picture = ({ name, alt, ...props }) => (
  <img src={asset(name)} width={imageSizes[name][0]} height={imageSizes[name][1]} alt={alt} loading="lazy" {...props} />
);
const featureImages = ["feature-data", "feature-compute", "feature-reason", "feature-open"];
const applicationImages = ["natural-resources", "ecology", "energy", "low-altitude"];

export default function AboutOpenGA() {
  const location = useLocation();
  useEffect(() => {
    const id = location.hash.slice(1);
    if (!id) return;
    // Run after the router restores scroll, including when the same anchor is clicked again.
    const frame = requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ block: "start" });
    });
    return () => cancelAnimationFrame(frame);
  }, [location.key, location.hash]);
  const english = localStorage.getItem("lang") === "en-US";
  const c = english ? en : zh;
  const platformName = english
    ? "Open Geospatial Agent (OpenGA)"
    : "时空智能体共享服务平台（Open Geospatial Agent，OpenGA）";
  const anchors = [
    ["overview", c.overview],
    ["features", c.featuresTitle],
    ["architecture", c.architectureTitle],
    ["applications", c.applicationsTitle],
    ["contact", c.contactTitle],
  ];
  return (
    <main className={styles.page}>
      <header className={styles.cover} style={{ "--cover-art": `url("${asset("cover-earth")}")` }}>
        <div className={styles.coverContent}>
          <span className={styles.wordmark}>OpenGA</span>
          <h1>{c.title}</h1>
          <p>Open Geospatial Agent</p>
          <div className={styles.actions}>
            <Link to="/infrastructure/openGA">{c.tryNow} →</Link>
          </div>
          <strong className={styles.tagline}>{c.tagline}</strong>
        </div>
      </header>
      <nav className={styles.sectionNav} aria-label={c.about}>
        {anchors.map(([id, text]) => (
          <Link key={id} to={`#${id}`}>
            {text}
          </Link>
        ))}
      </nav>
      <div className={styles.innerSpread}>
        <section
          id="overview"
          className={styles.intro}
          style={{ "--intro-art": `url("${asset("intro-earth")}")` }}
        >
          <h2>{c.overview}</h2>
          <Picture name="mascot" alt="OpenGA" className={styles.mascot} />
          {c.intro.map((text, index) => (
            <p key={index}>
              {index === 0 ? (
                <>
                  <strong className={styles.platformName}>{platformName}</strong>
                  {text.slice(platformName.length)}
                </>
              ) : (
                text
              )}
            </p>
          ))}
        </section>
        <section id="features" className={styles.features}>
          <h2>{c.featuresTitle}</h2>
          {c.features.map(([title, subtitle, body], index) => (
            <article key={title} className={styles.feature}>
              <div className={styles.featureHeading}>
                <Picture name={featureImages[index]} alt="" />
                <div>
                  <h3>{title}</h3>
                  <h4>{subtitle}</h4>
                </div>
              </div>
              <p>{body}</p>
            </article>
          ))}
        </section>
        <section id="architecture" className={styles.platform}>
          <h2>{c.architectureTitle}</h2>
          <article>
            <h3 className={styles.ribbon}>{english ? "Architecture" : "平台架构"}</h3>
            <div className={styles.architecture}>
              <p>
                {c.architectureLabel}
                {english ? ". " : "。"}
                {c.architecture}
              </p>
              <Picture name="architecture" alt={c.architectureLabel} />
            </div>
          </article>
          <article>
            <h3 className={styles.ribbon}>{c.librariesTitle}</h3>
            <p>{c.libraries}</p>
            <div className={styles.libraries}>
              <Picture name="geomcp" alt="GeoMCP" />
              <Picture name="knowledge" alt={c.libraryNames[1]} />
              <Picture name="skillhub" alt={c.libraryNames[2]} />
            </div>
          </article>
          <article>
            <h3 className={styles.ribbon}>{c.engineTitle}</h3>
            <p>{c.engine}</p>
            <ol className={styles.steps}>
              {c.steps.map(([title, body]) => (
                <li key={title}>
                  <h4>{title}</h4>
                  <p>{body}</p>
                </li>
              ))}
            </ol>
          </article>
          <article>
            <h3 className={styles.ribbon}>{c.skillsTitle}</h3>
            <p>{c.skills}</p>
            <ul className={styles.skillList}>
              {[0, 2, 4, 6, 8, 1, 3, 5, 7, 9].map((index) => (
                <li key={index}>
                  <span aria-hidden="true">◈</span>
                  {c.skillNames[index]}
                </li>
              ))}
            </ul>
          </article>
          <article>
            <h3 className={styles.ribbon}>{c.servicesTitle}</h3>
            <p>{c.services}</p>
            <div className={styles.services}>
              <Picture
                name="standards"
                alt={english ? "ISO and OGC standards" : "ISO与OGC国际标准"}
              />
              <Picture name="community" alt={english ? "OGE Alliance" : "开放时空智能社区"} />
            </div>
          </article>
        </section>
      </div>
      <section id="applications" className={styles.applications}>
        <h2>{c.applicationsTitle}</h2>
        <div className={styles.applicationGrid}>
          {c.applications.map(([title, subtitle, body, scenarios, value], index) => (
            <article className={styles.application} key={title}>
              <div>
                <h3>
                  <span aria-hidden="true">»</span> {title}
                  <span className={styles.separator}> | </span>
                  {subtitle}
                </h3>
                <p>{body}</p>
                <p>
                  {c.scenariosLabel}：{scenarios}
                </p>
                <p>
                  {c.valueLabel}：{value}
                </p>
              </div>
              <Picture name={applicationImages[index]} alt={title} />
            </article>
          ))}
        </div>
      </section>
      <section
        id="contact"
        className={styles.contact}
        style={{ "--map-art": `url("${asset("contact-map")}")` }}
      >
        <div className={styles.contactBrand}>
          <span>OpenGA</span>
          <h2>{c.title}</h2>
          <p>Open Geospatial Agent</p>
          <strong>{c.tagline}</strong>
        </div>
        <div className={styles.contactInfo}>
          <h2>{c.contactTitle}</h2>
          <p>
            {c.cooperation} <a href="tel:18986238461">18986238461</a>
          </p>
          <p>{c.support}</p>
          <p>
            <a href="https://oga.whu.edu.cn" target="_blank" rel="noreferrer">
              https://oga.whu.edu.cn
            </a>
          </p>
          <p>{c.address}</p>
          <figure>
            <Picture name="qrcode" alt={c.follow} />
            <figcaption>{c.follow}</figcaption>
          </figure>
        </div>
      </section>
    </main>
  );
}
